/**
 * Mobile scrolling utilities to fix common scrolling issues on mobile devices
 */

/**
 * Get mobile-optimized scrolling styles for containers
 * @returns {Object} CSS styles object for mobile scrolling
 */
export const getMobileScrollStyles = () => ({
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',
  minHeight: 0,
  flex: 1,
  overflowY: 'auto'
});

/**
 * Get mobile-optimized flex container styles
 * @returns {Object} CSS styles object for flex containers
 */
export const getMobileFlexStyles = () => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  overflow: 'hidden'
});

/**
 * Apply mobile scrolling fixes to an element
 * @param {HTMLElement} element - The element to apply fixes to
 */
export const applyMobileScrollFixes = (element) => {
  if (!element) return;
  
  // Apply touch scrolling
  element.style.WebkitOverflowScrolling = 'touch';
  element.style.overscrollBehavior = 'contain';
  
  // Ensure proper flex behavior
  if (element.classList.contains('flex-1')) {
    element.style.minHeight = '0';
  }
};

/**
 * Initialize mobile scrolling for all scrollable containers
 */
export const initializeMobileScrolling = () => {
  // Apply fixes to all overflow-y-auto elements
  const scrollableElements = document.querySelectorAll('.overflow-y-auto');
  scrollableElements.forEach(applyMobileScrollFixes);
  
  // Apply fixes to mobile dialog content
  const dialogElements = document.querySelectorAll('.mobile-dialog .overflow-y-auto');
  dialogElements.forEach(applyMobileScrollFixes);
};

/**
 * Mobile scroll event handler to prevent scroll chaining
 * @param {Event} event - The scroll event
 */
export const handleMobileScroll = (event) => {
  const element = event.target;
  const { scrollTop, scrollHeight, clientHeight } = element;
  
  // Prevent scroll chaining when at boundaries
  if (scrollTop === 0) {
    event.stopPropagation();
  } else if (scrollTop + clientHeight >= scrollHeight) {
    event.stopPropagation();
  }
};

/**
 * Add mobile scroll event listeners
 */
export const addMobileScrollListeners = () => {
  const scrollableElements = document.querySelectorAll('.overflow-y-auto, .mobile-dialog-scrollable');
  
  scrollableElements.forEach(element => {
    element.addEventListener('scroll', handleMobileScroll, { passive: true });
  });
};

/**
 * Remove mobile scroll event listeners
 */
export const removeMobileScrollListeners = () => {
  const scrollableElements = document.querySelectorAll('.overflow-y-auto, .mobile-dialog-scrollable');
  
  scrollableElements.forEach(element => {
    element.removeEventListener('scroll', handleMobileScroll);
  });
};

/**
 * Mobile scroll utility for React components
 */
export const useMobileScrolling = () => {
  const scrollStyles = getMobileScrollStyles();
  const flexStyles = getMobileFlexStyles();
  
  return {
    scrollStyles,
    flexStyles,
    applyFixes: applyMobileScrollFixes,
    initialize: initializeMobileScrolling,
    addListeners: addMobileScrollListeners,
    removeListeners: removeMobileScrollListeners
  };
};